import {
  BulkDialogPhaseType,
  BulkDialogProps,
  useBulkDialog,
} from "@/components/Dialog/helpers/DialogBulk/hooks";
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
  useRef,
} from "react";

/**
 * BulkDialogRerenderGuardProvider
 *
 * Firestore リアルタイム購読による親コンポーネントの再レンダリング連鎖から
 * BulkDialog の状態を保護するための Provider。
 *
 * 問題:
 * - GridTableData など複数の Firestore 購読を持つコンポーネントで物理削除を行うと
 * - 配列の変化により再レンダリングが発生し、open が一瞬 false になる
 * - useBulkDialog の useEffect[open] が発火し、状態がリセットされる
 *
 * 解決:
 * - open 状態を Context で管理し、親の再レンダリングから独立させる
 * - Provider を Firestore 購読より上位に配置することで安定性を確保
 * - bulkDialogArgs は openDialog 時に渡し、ref で保持して再レンダリングの影響を受けない
 *
 * Flow:
 * Consumer(openBulkDialog) -> Provider(storedArgsRef) -> BulkDialogRenderer -> useBulkDialog
 * useBulkDialog(onClose) -> Provider(closeBulkDialog) -> Consumer(onClose)
 */

// 内部で保持する bulkDialogArgs の型
type StoredBulkDialogArgs = Omit<
  // biome-ignore lint/suspicious/noExplicitAny: BulkDialogProps の制約 { [K in S]: string } との互換性のため
  BulkDialogProps<string, Record<string, any>>,
  "open"
> | null;

type BulkDialogRerenderGuardContextType = {
  isBulkDialogOpen: boolean;
  openBulkDialog: <
    S extends string,
    T extends Record<string, unknown> & { [K in S]: string },
  >(
    args: Omit<BulkDialogProps<S, T>, "open">
  ) => void;
  closeBulkDialog: () => void;
  phase: BulkDialogPhaseType;
};

const bulkDialogRerenderGuardContext =
  createContext<BulkDialogRerenderGuardContextType | null>(null);

export const useBulkDialogRerenderGuard = () => {
  const context = useContext(bulkDialogRerenderGuardContext);
  if (!context) {
    throw new Error("BulkDialogRerenderGuardProvider is required");
  }
  return context;
};

const BulkDialogRenderer: React.FC<{
  bulkDialogArgs: NonNullable<StoredBulkDialogArgs>;
  open: boolean;
  onCloseProvider: () => void;
}> = ({ bulkDialogArgs, open, onCloseProvider }) => {
  const argsWithCloseHandler = useMemo(
    () => ({
      ...bulkDialogArgs,
      onClose: (
        props: Parameters<NonNullable<StoredBulkDialogArgs>["onClose"]>[0]
      ) => {
        bulkDialogArgs.onClose(props);
        onCloseProvider();
      },
    }),
    [bulkDialogArgs, onCloseProvider]
  );

  const { renderBulkDialogs } = useBulkDialog({
    ...argsWithCloseHandler,
    open,
  });

  return <>{renderBulkDialogs()}</>;
};

export const BulkDialogRerenderGuardProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const [isBulkDialogOpen, setIsBulkDialogOpen] = useState(false);
  const [phase, setPhase] = useState<BulkDialogPhaseType>("confirm");

  // bulkDialogArgs を ref で保持（再レンダリングで変化しない）
  const storedArgsRef = useRef<StoredBulkDialogArgs>(null);

  const openBulkDialog = useCallback(
    <
      S extends string,
      T extends Record<string, unknown> & { [K in S]: string },
    >(
      args: Omit<BulkDialogProps<S, T>, "open">
    ) => {
      if (isBulkDialogOpen && phase === "in_progress") {
        return;
      }
      storedArgsRef.current = args as StoredBulkDialogArgs;
      setPhase("confirm");
      setIsBulkDialogOpen(true);
    },
    [isBulkDialogOpen, phase]
  );

  const closeBulkDialog = useCallback(() => {
    setIsBulkDialogOpen(false);
    setPhase("confirm");
    storedArgsRef.current = null;
  }, []);

  const contextValue = useMemo(
    () => ({
      isBulkDialogOpen,
      openBulkDialog,
      closeBulkDialog,
      phase,
    }),
    [isBulkDialogOpen, openBulkDialog, closeBulkDialog, phase]
  );

  return (
    <bulkDialogRerenderGuardContext.Provider value={contextValue}>
      {children}
      {isBulkDialogOpen && storedArgsRef.current && (
        <BulkDialogRenderer
          bulkDialogArgs={storedArgsRef.current}
          open={isBulkDialogOpen}
          onCloseProvider={closeBulkDialog}
        />
      )}
    </bulkDialogRerenderGuardContext.Provider>
  );
};
